import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";

import { AuthProvider } from "./hooks/useAuth";
import { RequireAuth } from "./components/RequireAuth";
import { SessionNotice } from "./components/SessionNotice";

// Public
import { Landing } from "./pages/Landing";
import { ForBusinessesPage } from "./pages/ForBusinessesPage";
import { ForInstitutionsPage } from "./pages/ForInstitutionsPage";
import { ComingSoonPage } from "./pages/ComingSoonPage";
import { TermsPage } from "./pages/TermsPage";
import { AccountDeletionPage } from "./pages/AccountDeletionPage";
import { PrivacyPage } from "./pages/PrivacyPage";
import { Login } from "./pages/Login";
import { Register } from "./pages/Register";
import { ForgotPasswordPage } from "./pages/ForgotPasswordPage";
import { ResetPasswordPage } from "./pages/ResetPasswordPage";
import { ChangePasswordPage } from "./pages/ChangePasswordPage";
import { AccountNotLinkedPage } from "./pages/AccountNotLinkedPage";
import { AcceptInvitePage } from "./pages/AcceptInvitePage";
import { DigiLockerCallbackPage } from "./pages/DigiLockerCallbackPage";
import { PaymentStatusPage } from "./pages/PaymentStatusPage";
//import for post job opputunuty and search job
import { PostOpportunityPage } from "./pages/PostOpportunityPage";
import { SearchJobsPage } from "./pages/SearchJobsPage";

// Shared (self-gated)
import { SharedProfilePage } from "./pages/SharedProfilePage";
import { AwaitingApprovalPage } from "./pages/AwaitingApprovalPage";

// Professional
import { Profile } from "./pages/Profile";
import { AddEducationPage } from "./pages/AddEducationPage";
import { AddWorkPage } from "./pages/AddWorkPage";
import { AddOtherPage } from "./pages/AddOtherPage";
import { BillingPage } from "./pages/BillingPage";
import { RequestVerificationPage } from "./pages/RequestVerificationPage";
import { CheckoutPage } from "./pages/CheckoutPage";
import { MyVerificationsPage } from "./pages/MyVerificationsPage";
import { CandidateRequestsPage } from "./pages/CandidateRequestsPage";

// Verifier (institution / organisation)
import { InstitutionDashboard } from "./pages/InstitutionDashboard";
import { VerificationInboxPage } from "./pages/VerificationInboxPage";
import { OfficersPage } from "./pages/OfficersPage";
import { EarningsPage } from "./pages/EarningsPage";

// Admin
import { AdminDashboard } from "./pages/AdminDashboard";
import { AdminPendingQueue } from "./pages/AdminPendingQueue";
import { AdminAuditLogPage } from "./pages/AdminAuditLogPage";
import { AdminCouponsPage } from "./pages/AdminCouponsPage";
import { AdminLegalPage } from "./pages/AdminLegalPage";

// Pre-launch gate (client request 24-07-2026): sign-in and sign-up are not
// open yet -- every auth entry point shows the Coming Soon page instead. Flip
// to true at launch to restore the real pages; nothing else needs to change.
const AUTH_LIVE = true;

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* -- Public -- */}
          <Route path="/" element={<Landing />} />
          <Route path="/business" element={<ForBusinessesPage />} />
          <Route path="/institutions" element={<ForInstitutionsPage />} />
          <Route path="/shop" element={<ComingSoonPage variant="shop" />} />
          <Route
            path="/gateway"
            element={<ComingSoonPage variant="gateway" />}
          />
          <Route
            path="/talent-hub"
            element={<ComingSoonPage variant="gateway" />}
          />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/terms-and-conditions" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/privacy-policy" element={<PrivacyPage />} />
          <Route path="/account-deletion" element={<AccountDeletionPage />} />
          <Route path="/coming-soon" element={<ComingSoonPage />} />
          <Route
            path="/login"
            element={AUTH_LIVE ? <Login /> : <ComingSoonPage variant="auth" />}
          />
          <Route
            path="/forgot-password"
            element={
              AUTH_LIVE ? (
                <ForgotPasswordPage />
              ) : (
                <ComingSoonPage variant="auth" />
              )
            }
          />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route
            path="/change-password"
            element={
              <RequireAuth>
                <ChangePasswordPage />
              </RequireAuth>
            }
          />
          <Route
            path="/register"
            element={
              AUTH_LIVE ? (
                <Register role="PROFESSIONAL" />
              ) : (
                <ComingSoonPage variant="auth" />
              )
            }
          />
          {/*
           * Officer sign-up is one flow (Register.jsx): identity check,
           * payment, then the account and the institution or organisation
           * together. The old ".../details" paths show an officer sign-in that
           * no institution or organisation belongs to (as the app does).
           */}
          <Route
            path="/register/institution"
            element={
              AUTH_LIVE ? (
                <Register role="INSTITUTION_OFFICER" />
              ) : (
                <ComingSoonPage variant="auth" />
              )
            }
          />
          <Route
            path="/register/institution/details"
            element={
              <RequireAuth>
                <AccountNotLinkedPage />
              </RequireAuth>
            }
          />
          <Route
            path="/register/organisation"
            element={
              AUTH_LIVE ? (
                <Register role="ORGANISATION_OFFICER" />
              ) : (
                <ComingSoonPage variant="auth" />
              )
            }
          />
          <Route
            path="/register/gst"
            element={<Navigate to="/register/organisation" replace />}
          />
          <Route
            path="/register/organisation/details"
            element={
              <RequireAuth>
                <AccountNotLinkedPage />
              </RequireAuth>
            }
          />
          <Route path="/accept-invite" element={<AcceptInvitePage />} />
          <Route
            path="/accept-invite-link"
            element={<Navigate to="/accept-invite" replace />}
          />
          <Route
            path="/verification/callback"
            element={<DigiLockerCallbackPage />}
          />
          {/* Cashfree's return address after a redirect-style payment */}
          <Route path="/payments/status" element={<PaymentStatusPage />} />
          <Route
            path="/verification/digilocker/callback"
            element={<DigiLockerCallbackPage />}
          />

          {/* -- Shared (self-gated) -- */}
          <Route path="/profile/:profileId" element={<SharedProfilePage />} />
          <Route path="/share/:token" element={<SharedProfilePage />} />
          <Route
            path="/candidates/requests/:requestId/profile"
            element={<SharedProfilePage />}
          />

          {/* -- Protected: holding page -- */}
          <Route
            path="/awaiting-approval"
            element={
              <RequireAuth>
                <AwaitingApprovalPage />
              </RequireAuth>
            }
          />

          {/* -- Protected: Professional -- */}
          <Route
            path="/profile"
            element={
              <RequireAuth>
                <Profile />
              </RequireAuth>
            }
          />
          <Route
            path="/billing"
            element={
              <RequireAuth>
                <BillingPage />
              </RequireAuth>
            }
          />
          <Route
            path="/credentials/new/education"
            element={
              <RequireAuth>
                <AddEducationPage />
              </RequireAuth>
            }
          />
          <Route
            path="/credentials/new/work"
            element={
              <RequireAuth>
                <AddWorkPage />
              </RequireAuth>
            }
          />
          <Route
            path="/credentials/new/other"
            element={
              <RequireAuth>
                <AddOtherPage />
              </RequireAuth>
            }
          />
          <Route
            path="/credentials/edit/education/:id"
            element={
              <RequireAuth>
                <AddEducationPage />
              </RequireAuth>
            }
          />
          <Route
            path="/credentials/edit/work/:id"
            element={
              <RequireAuth>
                <AddWorkPage />
              </RequireAuth>
            }
          />
          <Route
            path="/credentials/edit/other/:id"
            element={
              <RequireAuth>
                <AddOtherPage />
              </RequireAuth>
            }
          />
          <Route
            path="/credentials/view/education/:id"
            element={
              <RequireAuth>
                <AddEducationPage />
              </RequireAuth>
            }
          />
          <Route
            path="/credentials/view/work/:id"
            element={
              <RequireAuth>
                <AddWorkPage />
              </RequireAuth>
            }
          />
          <Route
            path="/credentials/view/other/:id"
            element={
              <RequireAuth>
                <AddOtherPage />
              </RequireAuth>
            }
          />
          <Route
            path="/verify/request"
            element={
              <RequireAuth>
                <RequestVerificationPage />
              </RequireAuth>
            }
          />
          <Route
            path="/checkout"
            element={
              <RequireAuth>
                <CheckoutPage />
              </RequireAuth>
            }
          />
          <Route
            path="/verifications"
            element={
              <RequireAuth>
                <MyVerificationsPage />
              </RequireAuth>
            }
          />
          <Route
            path="/search-jobs"
            element={
              <RequireAuth allowedTypes={["PROFESSIONAL"]}>
                <SearchJobsPage />
              </RequireAuth>
            }
          />

          {/* -- Protected: Institution / Organisation officer -- */}
          <Route
            path="/dashboard"
            element={
              <RequireAuth>
                <InstitutionDashboard />
              </RequireAuth>
            }
          />
          <Route
            path="/inbox"
            element={
              <RequireAuth>
                <VerificationInboxPage />
              </RequireAuth>
            }
          />
          <Route
            path="/post-opportunity"
            element={
              <RequireAuth>
                <PostOpportunityPage />
              </RequireAuth>
            }
          />
          <Route
            path="/officers"
            element={
              <RequireAuth>
                <OfficersPage />
              </RequireAuth>
            }
          />
          <Route
            path="/earnings"
            element={
              <RequireAuth>
                <EarningsPage />
              </RequireAuth>
            }
          />
          <Route
            path="/candidates"
            element={
              <RequireAuth>
                <CandidateRequestsPage />
              </RequireAuth>
            }
          />

          {/* -- Protected: Platform admin -- */}
          <Route
            path="/admin"
            element={
              <RequireAuth>
                <AdminDashboard />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/pending"
            element={
              <RequireAuth>
                <AdminPendingQueue />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/audit-log"
            element={
              <RequireAuth>
                <AdminAuditLogPage />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/coupons"
            element={
              <RequireAuth>
                <AdminCouponsPage />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/legal"
            element={
              <RequireAuth>
                <AdminLegalPage />
              </RequireAuth>
            }
          />

          {/* -- Catch-all -- */}
          <Route path="*" element={<Landing />} />
        </Routes>
        <SessionNotice />
      </BrowserRouter>
    </AuthProvider>
  );
}
